import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { useUnit } from "@/context/UnitContext";
import { isTempSensor, toDisplayTemp, fromDisplayTemp, roundTemp, tempSymbol } from "@/lib/units";
import { STATUS_META, STATUS_ORDER, subTypeLabel } from "@/lib/constants";
import { downloadCSV } from "@/lib/export";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  AlertTriangle, FileText, CalendarClock, ShieldCheck, TrendingUp, Activity, Loader2, Plus, Cpu, LineChart as LineChartIcon, Download,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Area, AreaChart, ReferenceLine,
} from "recharts";

const SENSOR_COLORS = { pm25: "#2E7599", noise: "#B84A22", wbgt: "#D98014", voc: "#6D9C55" };
const inputCls = "w-full bg-[#F8FAF8] border border-[#DCE3DD] rounded-sm px-3 py-2 text-sm focus:ring-2 focus:ring-[#275C3F] focus:ring-offset-1 outline-none";

function StatCard({ icon: Icon, label, value, sub, tone = "#275C3F", testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#DCE3DD] rounded-sm p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5950]">{label}</span>
        <Icon className="h-4 w-4" style={{ color: tone }} />
      </div>
      <p className="font-display text-3xl font-bold mt-3" style={{ color: tone }}>{value}</p>
      {sub && <p className="text-sm text-[#4B5950] mt-1">{sub}</p>}
    </div>
  );
}

function SensorTile({ t: tile, tr, onOpen }) {
  const { tempUnit } = useUnit();
  const color = SENSOR_COLORS[tile.key] || "#275C3F";
  const isTemp = isTempSensor(tile.key);
  const conv = (v) => (isTemp ? roundTemp(toDisplayTemp(v, tempUnit)) : v);
  const unitLabel = isTemp ? tempSymbol(tempUnit) : tile.unit;
  const data = tile.series.map((v, i) => ({ i, v: conv(v) }));
  const alert = tile.value >= tile.threshold;
  const displayValue = conv(tile.value);
  const displayThreshold = conv(tile.threshold);
  return (
    <button data-testid={`sensor-${tile.key}`} onClick={() => onOpen(tile)} className="text-left w-full bg-white border border-[#DCE3DD] rounded-sm p-4 hover:border-[#275C3F]/50 hover:shadow-sm transition-all duration-200 cursor-pointer">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-[#4B5950]">{tile.label}</span>
        <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm ${alert ? "bg-[#B84A22]/15 text-[#B84A22]" : "bg-[#6D9C55]/15 text-[#4B6B3A]"}`}>
          {alert ? tr("dash.over") : tr("dash.ok")}
        </span>
      </div>
      <p className="font-display text-2xl font-bold mt-1" style={{ color }}>
        {displayValue}<span className="text-sm text-[#4B5950] font-normal ml-1">{unitLabel}</span>
      </p>
      <p className="text-xs text-[#4B5950]">{tr("dash.threshold")} {displayThreshold} {unitLabel}</p>
      <div className="h-12 mt-2">
        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
          <AreaChart data={data} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
            <Area type="monotone" dataKey="v" stroke={color} fill={color} fillOpacity={0.15} strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </button>
  );
}

function fmtTime(iso) {
  try {
    const d = new Date(iso);
    return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  } catch { return ""; }
}

function SensorHistoryDialog({ tile, data, loading, onClose, tr }) {
  const { tempUnit } = useUnit();
  if (!tile) return null;
  const isTemp = isTempSensor(tile.key);
  const conv = (v) => (isTemp ? roundTemp(toDisplayTemp(v, tempUnit)) : v);
  const unitLabel = isTemp ? tempSymbol(tempUnit) : tile.unit;
  const color = SENSOR_COLORS[tile.key] || "#275C3F";
  const readings = data?.readings || [];
  const chartData = readings.map((r) => ({ t: fmtTime(r.recorded_at), v: conv(r.value) }));
  const displayThreshold = conv(tile.threshold);
  const breaches = readings.filter((r) => r.value >= tile.threshold).length;
  const latest = readings.length ? conv(readings[readings.length - 1].value) : null;

  const exportCsv = () => {
    downloadCSV(
      `${tile.key}-history.csv`,
      [
        { label: "Recorded At", get: (r) => r.recorded_at },
        { label: `Value (${unitLabel})`, get: (r) => conv(r.value) },
        { label: `Threshold (${unitLabel})`, get: () => displayThreshold },
        { label: "Breach", get: (r) => (r.value >= tile.threshold ? "yes" : "no") },
      ],
      readings
    );
  };

  return (
    <Dialog open={!!tile} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="sensor-history-dialog" className="sm:max-w-2xl">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4 pr-6">
            <div>
              <DialogTitle className="font-display flex items-center gap-2"><LineChartIcon className="h-5 w-5 text-[#275C3F]" /> {tile.label} — {tr("dash.history")}</DialogTitle>
              <DialogDescription className="mt-1">{tr("dash.historyDesc")}</DialogDescription>
            </div>
            {readings.length > 0 && (
              <button data-testid="sensor-history-export" onClick={exportCsv} className="shrink-0 inline-flex items-center gap-1.5 border border-[#DCE3DD] bg-white px-3 py-1.5 rounded-sm text-xs font-medium hover:bg-[#EAF0EA] transition-all duration-200">
                <Download className="h-3.5 w-3.5" /> {tr("dash.exportCsv")}
              </button>
            )}
          </div>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-[#275C3F]" /></div>
        ) : readings.length === 0 ? (
          <p data-testid="sensor-history-empty" className="text-sm text-[#4B5950] py-16 text-center">{tr("dash.noHistory")}</p>
        ) : (
          <div>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-[#F8FAF8] border border-[#DCE3DD] rounded-sm p-3">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5950]">{tr("dash.latest")}</p>
                <p className="font-display text-xl font-bold" style={{ color }}>{latest} <span className="text-xs font-normal text-[#4B5950]">{unitLabel}</span></p>
              </div>
              <div className="bg-[#F8FAF8] border border-[#DCE3DD] rounded-sm p-3">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5950]">{tr("dash.readings")}</p>
                <p className="font-display text-xl font-bold text-[#275C3F]">{readings.length}</p>
              </div>
              <div className="bg-[#F8FAF8] border border-[#DCE3DD] rounded-sm p-3">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-[#4B5950]">{tr("dash.breaches")}</p>
                <p className="font-display text-xl font-bold text-[#B84A22]">{breaches}</p>
              </div>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 30 }}>
                  <XAxis dataKey="t" angle={-30} textAnchor="end" tick={{ fontSize: 10, fill: "#4B5950" }} interval="preserveStartEnd" minTickGap={20} />
                  <YAxis tick={{ fontSize: 11, fill: "#4B5950" }} />
                  <Tooltip formatter={(v) => [`${v} ${unitLabel}`, tile.label]} />
                  <ReferenceLine y={displayThreshold} stroke="#B84A22" strokeDasharray="4 4" label={{ value: `${tr("dash.threshold")} ${displayThreshold}`, fontSize: 10, fill: "#B84A22", position: "insideTopRight" }} />
                  <Area type="monotone" dataKey="v" stroke={color} fill={color} fillOpacity={0.15} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useLang();
  const { tempUnit } = useUnit();
  const [stats, setStats] = useState(null);
  const [sensors, setSensors] = useState(null);
  const [open, setOpen] = useState(false);
  const [reading, setReading] = useState({ sensor_key: "pm25", value: "" });
  const [saving, setSaving] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [histTile, setHistTile] = useState(null);
  const [hist, setHist] = useState(null);
  const [histLoading, setHistLoading] = useState(false);

  const loadSensors = () => api.get("/sensors").then((r) => setSensors(r.data)).catch(() => {});
  const loadStats = () => api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { loadStats(); loadSensors(); }, []);

  const openHistory = async (tile) => {
    setHistTile(tile);
    setHist(null);
    setHistLoading(true);
    try {
      const { data } = await api.get(`/sensors/history/${tile.key}?limit=100`);
      setHist(data);
    } catch {
      setHist({ readings: [] });
    } finally { setHistLoading(false); }
  };

  const simulateDevice = async () => {
    if (simulating || !sensors) return;
    setSimulating(true);
    const sleep = (ms) => new Promise((res) => setTimeout(res, ms));
    try {
      const { data: settings } = await api.get("/settings");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/ingest/readings`;
      const tiles = sensors.tiles;
      const seq = [];
      for (let i = 0; i < 5; i++) {
        const tile = tiles[i % tiles.length];
        seq.push({ key: tile.key, value: Math.round(tile.threshold * (0.4 + Math.random() * 0.45) * 10) / 10 });
      }
      const noise = tiles.find((x) => x.key === "noise") || tiles[0];
      seq.push({ key: noise.key, value: Math.round(noise.threshold * 1.12 * 10) / 10 }); // guaranteed breach
      let incidents = 0;
      for (const r of seq) {
        try {
          const resp = await fetch(url, {
            method: "POST",
            headers: { "X-API-Key": settings.api_key, "Content-Type": "application/json" },
            body: JSON.stringify({ sensor_key: r.key, value: r.value }),
          });
          const d = await resp.json();
          if (d.incident_created) incidents++;
        } catch {}
        loadSensors();
        await sleep(850);
      }
      loadStats();
      toast.success(t("dash.simDone"));
      if (incidents > 0) toast.info(t("dash.incidentCreated"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSimulating(false); }
  };

  const submitReading = async () => {
    if (reading.value === "" || isNaN(Number(reading.value))) { toast.error(t("dash.value")); return; }
    setSaving(true);
    try {
      let value = Number(reading.value);
      if (isTempSensor(reading.sensor_key)) value = roundTemp(fromDisplayTemp(value, tempUnit));
      const { data } = await api.post("/sensors/readings", { sensor_key: reading.sensor_key, value });
      if (data.breach) {
        toast.warning(t("dash.breach"));
        if (data.incident_created) toast.info(t("dash.incidentCreated"));
        loadStats();
      } else {
        toast.success(t("dash.readingSaved"));
      }
      setOpen(false);
      setReading({ sensor_key: "pm25", value: "" });
      loadSensors();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (!stats || !sensors) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-[#275C3F]" /></div>;
  }

  const barData = stats.by_subtype.map((b) => ({ name: subTypeLabel(b.name, t), count: b.count }));

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      <div>
        <h1 className="font-display text-3xl font-bold">{t("dash.welcome")}, {user?.name?.split(" ")[0] || ""}.</h1>
        <p className="text-[#4B5950] mt-1">{t("dash.subtitle")}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-open-incidents" icon={AlertTriangle} label={t("dash.openIncidents")} value={stats.open_incidents} sub={`${stats.total_incidents} ${t("dash.total")}`} tone="#B84A22" />
        <StatCard testid="stat-overdue" icon={CalendarClock} label={t("dash.overdueItems")} value={stats.overdue} sub={`${stats.due_soon} ${t("dash.dueSoon")}`} tone="#D98014" />
        <StatCard testid="stat-documents" icon={FileText} label={t("dash.documents")} value={stats.documents} sub={t("dash.analyzedStored")} tone="#2E7599" />
        <StatCard testid="stat-compliance-rate" icon={ShieldCheck} label={t("dash.compliance")} value={`${stats.compliance_rate}%`} sub={t("dash.onTrackRate")} tone="#275C3F" />
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl font-semibold flex items-center gap-2"><Activity className="h-5 w-5 text-[#275C3F]" /> {t("dash.liveConditions")}</h2>
          <div className="flex items-center gap-2">
            <button data-testid="simulate-device-button" onClick={simulateDevice} disabled={simulating} className="inline-flex items-center gap-2 border border-[#2E7599]/40 text-[#2E7599] px-3 py-2 rounded-sm text-sm font-medium hover:bg-[#2E7599]/10 transition-all duration-200 disabled:opacity-60">
              {simulating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Cpu className="h-4 w-4" />} {simulating ? t("dash.simulating") : t("dash.simulate")}
            </button>
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="log-reading-button" className="inline-flex items-center gap-2 border border-[#DCE3DD] bg-white px-3 py-2 rounded-sm text-sm font-medium hover:bg-[#EAF0EA] transition-all duration-200">
                <Plus className="h-4 w-4" /> {t("dash.logReading")}
              </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-sm">
              <DialogHeader>
                <DialogTitle className="font-display">{t("dash.addReading")}</DialogTitle>
                <DialogDescription>{t("dash.liveConditions")}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium">{t("dash.sensor")}</label>
                  <select data-testid="reading-sensor-select" className={inputCls} value={reading.sensor_key} onChange={(e) => setReading({ ...reading, sensor_key: e.target.value })}>
                    {sensors.tiles.map((tile) => <option key={tile.key} value={tile.key}>{tile.label} ({isTempSensor(tile.key) ? tempSymbol(tempUnit) : tile.unit})</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">{t("dash.value")}</label>
                  <input data-testid="reading-value-input" type="number" step="any" className={inputCls} value={reading.value} onChange={(e) => setReading({ ...reading, value: e.target.value })} placeholder="0.0" />
                </div>
              </div>
              <DialogFooter>
                <button data-testid="submit-reading-button" onClick={submitReading} disabled={saving} className="inline-flex items-center gap-2 bg-[#275C3F] hover:bg-[#1E4731] text-white px-4 py-2 rounded-sm text-sm font-semibold disabled:opacity-60">
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />} {t("common.add")}
                </button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {sensors.tiles.map((tile) => <SensorTile key={tile.key} t={tile} tr={t} onOpen={openHistory} />)}
        </div>
      </div>

      <SensorHistoryDialog tile={histTile} data={hist} loading={histLoading} onClose={() => setHistTile(null)} tr={t} />

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#DCE3DD] rounded-sm p-5">
          <h2 className="font-display text-lg font-semibold flex items-center gap-2 mb-4"><TrendingUp className="h-5 w-5 text-[#275C3F]" /> {t("dash.incidentsByType")}</h2>
          {barData.length === 0 ? (
            <p className="text-sm text-[#4B5950] py-10 text-center">{t("dash.noIncidents")}</p>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                <BarChart data={barData} margin={{ top: 10, right: 10, left: -20, bottom: 40 }}>
                  <XAxis dataKey="name" angle={-30} textAnchor="end" tick={{ fontSize: 11, fill: "#4B5950" }} interval={0} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#4B5950" }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#275C3F" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="bg-white border border-[#DCE3DD] rounded-sm p-5">
          <h2 className="font-display text-lg font-semibold mb-4">{t("dash.lifecyclePipeline")}</h2>
          <div className="space-y-3">
            {STATUS_ORDER.map((s) => {
              const m = STATUS_META[s];
              const count = stats.by_status[s] || 0;
              const max = Math.max(1, ...Object.values(stats.by_status));
              return (
                <div key={s}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-medium" style={{ color: m.color }}>{t(`status.${s}`)}</span>
                    <span className="text-[#4B5950]">{count}</span>
                  </div>
                  <div className="h-2 bg-[#EAF0EA] rounded-sm overflow-hidden">
                    <div className="h-full rounded-sm transition-all duration-500" style={{ width: `${(count / max) * 100}%`, background: m.bg }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
